import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Card,
  CardContent,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Skeleton,
  Fade,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  alpha,
  useTheme,
  MenuItem
} from '@mui/material';
import {
  Backup,
  Restore,
  Download,
  Delete,
  Schedule,
  CloudUpload,
  Storage,
  CheckCircle,
  Error,
  Info,
  Refresh,
  Add
} from '@mui/icons-material';
import api from '../services/api';

const BackupRestore = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [backups, setBackups] = useState([]);

  const [loading, setLoading] = useState(true);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [schedules, setSchedules] = useState([]);
  const [showScheduleDialog, setShowScheduleDialog] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({
    name: '',
    frequency: 'daily',
    time: '02:00',
    storageType: 'local',
    collections: ['users', 'complaints', 'categories', 'settings', 'assemblies', 'districts'],
    encryption: { enabled: false, password: '' },
    compression: { enabled: true, level: 6 },
    validation: { enabled: true },
    isActive: true
  });
  
  // Manual backup options
  const [encryption, setEncryption] = useState(false);
  const [compression, setCompression] = useState(true);
  const [validation, setValidation] = useState(true);
  const [encryptionPassword, setEncryptionPassword] = useState('');
  const [compressionLevel, setCompressionLevel] = useState(6);

  const theme = useTheme();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [backupsRes, schedulesRes] = await Promise.all([
        api.get('/admin/backups'),
        api.get('/admin/backup-schedules')
      ]);
      setBackups(backupsRes.data);
      setSchedules(schedulesRes.data);
    } catch (err) {
      setError('Failed to load backup data');
      console.error('Error fetching backup data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleManualBackup = async () => {
    try {
      setCreatingBackup(true);
      setError(null);
      
      const backupOptions = {
        name: `Manual_Backup_${new Date().toISOString().split('T')[0]}`,
        description: 'Manual backup created by admin',
        collections: ['users', 'complaints', 'categories', 'settings', 'assemblies', 'districts'],
        encryption: encryption ? { enabled: true, password: encryptionPassword } : { enabled: false },
        compression: compression ? { enabled: true, level: compressionLevel } : { enabled: false },
        validation: validation ? { enabled: true } : { enabled: false }
      };

      await api.post('/admin/backup', backupOptions);
      setSuccess('Backup created successfully!');
      fetchData();
    } catch (err) {
      setError('Failed to create backup');
      console.error('Error creating backup:', err);
    } finally {
      setCreatingBackup(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedFile) return;
    
    try {
      setRestoring(true);
      setError(null);
      
      const formData = new FormData();
      formData.append('backupFile', selectedFile);
      
      await api.post('/admin/restore', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setSuccess('Backup restored successfully!');
      setShowRestoreDialog(false);
      setSelectedFile(null);
    } catch (err) {
      setError('Failed to restore backup');
      console.error('Error restoring backup:', err);
    } finally {
      setRestoring(false);
    }
  };

  const handleDeleteBackup = async (backupId) => {
    try {
      await api.delete(`/admin/backups/${backupId}`);
      setSuccess('Backup deleted successfully!');
      fetchData();
    } catch (err) {
      setError('Failed to delete backup');
      console.error('Error deleting backup:', err);
    }
  };

  const handleDownloadBackup = async (backupId) => {
    try {
      const response = await api.get(`/admin/backups/${backupId}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `backup-${backupId}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Failed to download backup');
      console.error('Error downloading backup:', err);
    }
  };

  const handleCreateSchedule = async () => {
    try {
      setError(null);
      
      await api.post('/admin/backup-schedules', scheduleForm);
      setSuccess('Backup schedule created successfully!');
      setShowScheduleDialog(false);
      fetchData();
    } catch (err) {
      setError('Failed to create backup schedule');
      console.error('Error creating backup schedule:', err);
    }
  };

  const handleOpenScheduleDialog = () => {
    setScheduleForm({
      name: `Scheduled_Backup_${new Date().toISOString().split('T')[0]}`,
      frequency: 'daily',
      time: '02:00',
      storageType: 'local',
      collections: ['users', 'complaints', 'categories', 'settings', 'assemblies', 'districts'],
      encryption: { enabled: false, password: '' },
      compression: { enabled: true, level: 6 },
      validation: { enabled: true },
      isActive: true
    });
    setShowScheduleDialog(true);
  };

  const handleCloseScheduleDialog = () => {
    setShowScheduleDialog(false);
  };

  const handleDeleteSchedule = async (scheduleId) => {
    try {
      await api.delete(`/admin/backup-schedules/${scheduleId}`);
      setSuccess('Backup schedule deleted successfully!');
      fetchData();
    } catch (err) {
      setError('Failed to delete backup schedule');
      console.error('Error deleting backup schedule:', err);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle color="success" />;
      case 'failed':
        return <Error color="error" />;
      case 'running':
        return <CircularProgress size={20} />;
      default:
        return <Info color="info" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'failed':
        return 'error';
      case 'running':
        return 'warning';
      default:
        return 'info';
    }
  };

  // Loading skeleton
  const BackupItemSkeleton = () => (
    <Card sx={{ mb: 2, borderRadius: 2 }}>
      <CardContent sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            <Skeleton variant="text" width="60%" height={24} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="40%" height={16} />
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="circular" width={32} height={32} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Fade in timeout={300}>
        <Box>
          <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
            Backup & Restore
          </Typography>
          
          <Tabs value={0} sx={{ mb: 3 }}>
            <Tab label="Manual Backup" />
            <Tab label="Restore" />
            <Tab label="Scheduled Backups" />
          </Tabs>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={8}>
              {[1, 2, 3].map((item) => (
                <BackupItemSkeleton key={item} />
              ))}
            </Grid>
            <Grid item xs={12} md={4}>
              <Card sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Skeleton variant="text" width="80%" height={32} sx={{ mb: 2 }} />
                  <Skeleton variant="text" width="100%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="60%" height={20} sx={{ mb: 1 }} />
                  <Skeleton variant="text" width="40%" height={20} />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Box>
      </Fade>
    );
  }

  return (
    <Fade in timeout={500}>
      <Box>
        {/* Header */}
        <Slide direction="down" in timeout={600}>
          <Box sx={{ mb: 4 }}>
            <Typography 
              variant="h4" 
              gutterBottom
              sx={{ 
                fontWeight: 700,
                background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Backup & Restore
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage system backups, restore data, and configure automated backup schedules
            </Typography>
          </Box>
        </Slide>

        {/* Alerts */}
        {error && (
          <Slide direction="down" in timeout={400}>
            <Alert 
              severity="error" 
              sx={{ mb: 3 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          </Slide>
        )}

        {success && (
          <Slide direction="down" in timeout={400}>
            <Alert 
              severity="success" 
              sx={{ mb: 3 }}
              onClose={() => setSuccess(null)}
            >
              {success}
            </Alert>
          </Slide>
        )}

        {/* Tabs */}
        <Slide direction="up" in timeout={700}>
          <Tabs 
            value={activeTab} 
            onChange={(e, newValue) => setActiveTab(newValue)}
            sx={{ 
              mb: 3,
              '& .MuiTab-root': {
                fontWeight: 600,
                textTransform: 'none',
                fontSize: '1rem'
              }
            }}
          >
            <Tab label="Manual Backup" icon={<Backup />} iconPosition="start" />
            <Tab label="Restore" icon={<Restore />} iconPosition="start" />
            <Tab label="Scheduled Backups" icon={<Schedule />} iconPosition="start" />
          </Tabs>
        </Slide>

        {/* Tab Content */}
        <Slide direction="up" in timeout={800}>
          <Box>
            {activeTab === 0 && (
              <Grid container spacing={3}>
                {/* Manual Backup Options */}
                <Grid item xs={12} md={4}>
                  <Card sx={{ borderRadius: 3, height: 'fit-content' }}>
                    <CardContent sx={{ p: 3 }}>
                      <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                        Backup Options
                      </Typography>
                      
                      <Box sx={{ mb: 3 }}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={encryption} 
                              onChange={(e) => setEncryption(e.target.checked)}
                            />
                          }
                          label="Encryption"
                        />
                        {encryption && (
                          <TextField
                            fullWidth
                            label="Encryption Password"
                            type="password"
                            value={encryptionPassword}
                            onChange={(e) => setEncryptionPassword(e.target.value)}
                            sx={{ mt: 2 }}
                            size="small"
                          />
                        )}
                      </Box>

                      <Box sx={{ mb: 3 }}>
                        <FormControlLabel
                          control={
                            <Switch 
                              checked={compression} 
                              onChange={(e) => setCompression(e.target.checked)}
                            />
                          }
                          label="Compression"
                        />
                        {compression && (
                          <TextField
                            fullWidth
                            label="Compression Level"
                            type="number"
                            value={compressionLevel}
                            onChange={(e) => setCompressionLevel(e.target.value)}
                            inputProps={{ min: 1, max: 9 }}
                            sx={{ mt: 2 }}
                            size="small"
                          />
                        )}
                      </Box>

                      <FormControlLabel
                        control={
                          <Switch 
                            checked={validation} 
                            onChange={(e) => setValidation(e.target.checked)}
                          />
                        }
                        label="Validation"
                      />

                      <Button
                        fullWidth
                        variant="contained"
                        onClick={handleManualBackup}
                        disabled={creatingBackup}
                        startIcon={creatingBackup ? <CircularProgress size={20} /> : <Backup />}
                        sx={{ mt: 3, py: 1.5, fontWeight: 600 }}
                      >
                        {creatingBackup ? 'Creating Backup...' : 'Create Backup'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Backup List */}
                <Grid item xs={12} md={8}>
                  <Card sx={{ borderRadius: 3 }}>
                    <CardContent sx={{ p: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Recent Backups
                        </Typography>
                        <IconButton onClick={fetchData} size="small">
                          <Refresh />
                        </IconButton>
                      </Box>
                      
                      {backups.length === 0 ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Storage sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="h6" color="text.secondary" gutterBottom>
                            No backups found
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Create your first backup to get started
                          </Typography>
                        </Box>
                      ) : (
                        <List>
                          {backups.map((backup) => (
                            <ListItem 
                              key={backup._id}
                              sx={{ 
                                borderRadius: 2, 
                                mb: 1,
                                backgroundColor: alpha(theme.palette.background.default, 0.5),
                                '&:hover': {
                                  backgroundColor: alpha(theme.palette.background.default, 0.8)
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
                                {getStatusIcon(backup.status)}
                                <Box sx={{ ml: 2, flex: 1 }}>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {backup.name || `Backup ${backup._id}`}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {new Date(backup.createdAt).toLocaleString()}
                                  </Typography>
                                </Box>
                                <Chip 
                                  label={backup.status} 
                                  color={getStatusColor(backup.status)}
                                  size="small"
                                  sx={{ mr: 2 }}
                                />
                              </Box>
                              <ListItemSecondaryAction>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title="Download">
                                    <IconButton 
                                      size="small"
                                      onClick={() => handleDownloadBackup(backup._id)}
                                    >
                                      <Download />
                                    </IconButton>
                                  </Tooltip>
                                  <Tooltip title="Delete">
                                    <IconButton 
                                      size="small"
                                      color="error"
                                      onClick={() => handleDeleteBackup(backup._id)}
                                    >
                                      <Delete />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            )}

            {activeTab === 1 && (
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Restore from Backup
                  </Typography>
                  
                  <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Restore sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                      Select Backup File
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Choose a backup file to restore your system data
                    </Typography>
                    
                    <Button
                      variant="contained"
                      component="label"
                      startIcon={<CloudUpload />}
                      sx={{ mr: 2 }}
                    >
                      Choose File
                      <input
                        type="file"
                        hidden
                        accept=".zip,.json"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                      />
                    </Button>
                    
                    {selectedFile && (
                      <Button
                        variant="contained"
                        color="success"
                        onClick={() => setShowRestoreDialog(true)}
                        startIcon={<Restore />}
                      >
                        Restore Backup
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            )}

            {activeTab === 2 && (
              <Card sx={{ borderRadius: 3 }}>
                <CardContent sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                    Scheduled Backups
                  </Typography>
                  
                  {schedules.length === 0 ? (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Schedule sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                      <Typography variant="h6" color="text.secondary" gutterBottom>
                        No schedules configured
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Create automated backup schedules to ensure data safety
                      </Typography>
                      
                      <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={handleOpenScheduleDialog}
                      >
                        Create Schedule
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle1" color="text.secondary">
                          {schedules.length} schedule{schedules.length !== 1 ? 's' : ''} configured
                        </Typography>
                        <Button
                          variant="contained"
                          size="small"
                          startIcon={<Add />}
                          onClick={handleOpenScheduleDialog}
                        >
                          Add Schedule
                        </Button>
                      </Box>
                      
                      <List>
                        {schedules.map((schedule) => (
                          <ListItem
                            key={schedule._id}
                            sx={{
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 2,
                              mb: 1,
                              '&:hover': {
                                backgroundColor: 'action.hover'
                              }
                            }}
                          >
                            <ListItemText
                              primary={schedule.name}
                              secondary={`${schedule.frequency} at ${schedule.time} • ${schedule.isActive ? 'Active' : 'Inactive'}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                aria-label="delete"
                                onClick={() => handleDeleteSchedule(schedule._id)}
                              >
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        </Slide>

        {/* Restore Confirmation Dialog */}
        <Dialog open={showRestoreDialog} onClose={() => setShowRestoreDialog(false)}>
          <DialogTitle>Confirm Restore</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to restore from this backup? This action will overwrite current data.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowRestoreDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleRestore} 
              color="primary" 
              variant="contained"
              disabled={restoring}
            >
              {restoring ? 'Restoring...' : 'Restore'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Schedule Creation Dialog */}
        <Dialog open={showScheduleDialog} onClose={handleCloseScheduleDialog}>
          <DialogTitle>Create New Backup Schedule</DialogTitle>
          <DialogContent>
            <Box sx={{ mt: 2 }}>
              <TextField
                fullWidth
                label="Schedule Name"
                value={scheduleForm.name}
                onChange={(e) => setScheduleForm({ ...scheduleForm, name: e.target.value })}
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={
                  <Switch 
                    checked={scheduleForm.isActive} 
                    onChange={(e) => setScheduleForm({ ...scheduleForm, isActive: e.target.checked })}
                  />
                }
                label="Active"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                If inactive, this schedule will not run automatically.
              </Typography>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Backup Options
              </Typography>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={scheduleForm.encryption.enabled} 
                      onChange={(e) => setScheduleForm({ ...scheduleForm, encryption: { ...scheduleForm.encryption, enabled: e.target.checked } })}
                    />
                  }
                  label="Encryption"
                />
                {scheduleForm.encryption.enabled && (
                  <TextField
                    fullWidth
                    label="Encryption Password"
                    type="password"
                    value={scheduleForm.encryption.password}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, encryption: { ...scheduleForm.encryption, password: e.target.value } })}
                    sx={{ mt: 2 }}
                    size="small"
                  />
                )}
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControlLabel
                  control={
                    <Switch 
                      checked={scheduleForm.compression.enabled} 
                      onChange={(e) => setScheduleForm({ ...scheduleForm, compression: { ...scheduleForm.compression, enabled: e.target.checked } })}
                    />
                  }
                  label="Compression"
                />
                {scheduleForm.compression.enabled && (
                  <TextField
                    fullWidth
                    label="Compression Level"
                    type="number"
                    value={scheduleForm.compression.level}
                    onChange={(e) => setScheduleForm({ ...scheduleForm, compression: { ...scheduleForm.compression, level: e.target.value } })}
                    inputProps={{ min: 1, max: 9 }}
                    sx={{ mt: 2 }}
                    size="small"
                  />
                )}
              </Box>

              <FormControlLabel
                control={
                  <Switch 
                    checked={scheduleForm.validation.enabled} 
                    onChange={(e) => setScheduleForm({ ...scheduleForm, validation: { ...scheduleForm.validation, enabled: e.target.checked } })}
                  />
                }
                label="Validation"
              />

                             <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                 Schedule Details
               </Typography>

              <TextField
                fullWidth
                label="Frequency"
                select
                value={scheduleForm.frequency}
                onChange={(e) => setScheduleForm({ ...scheduleForm, frequency: e.target.value })}
                sx={{ mb: 2 }}
              >
                <MenuItem value="daily">Daily</MenuItem>
                <MenuItem value="weekly">Weekly</MenuItem>
                <MenuItem value="monthly">Monthly</MenuItem>
              </TextField>

              <TextField
                fullWidth
                label="Time"
                type="time"
                value={scheduleForm.time}
                onChange={(e) => setScheduleForm({ ...scheduleForm, time: e.target.value })}
                inputProps={{ step: 300 }}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Collections to Backup"
                select
                multiple
                value={scheduleForm.collections}
                onChange={(e) => setScheduleForm({ ...scheduleForm, collections: Array.from(e.target.value) })}
                sx={{ mb: 2 }}
              >
                <MenuItem value="users">Users</MenuItem>
                <MenuItem value="complaints">Complaints</MenuItem>
                <MenuItem value="categories">Categories</MenuItem>
                <MenuItem value="settings">Settings</MenuItem>
                <MenuItem value="assemblies">Assemblies</MenuItem>
                <MenuItem value="districts">Districts</MenuItem>
              </TextField>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseScheduleDialog}>Cancel</Button>
            <Button 
              onClick={handleCreateSchedule} 
              color="primary" 
              variant="contained"
            >
              Create Schedule
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Fade>
  );
};

export default BackupRestore; 